import { App, Button, Link, Skeleton, Spin, Tooltip } from "@/atoms"
import { isPackageUpdate } from "@/domain/issue"
import { useLogger } from "@/hooks/useLogger"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { useTranslation } from "@/hooks/useTranslation"
import { ArrowRightOutlined, BaseImage, Package, QuickWinBolt } from "@/icons"
import { IssuesSummaryFC } from "@/molecules/IssuesSummaryFC"
import { useQuickWinIssues } from "@/pages/TriagePage/application/useQuickWins"
import { buildQuickWinCopyText } from "@/pages/TriagePage/application/useQuickWins.selector"
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons"
import cn from "classnames"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import CopyToClipboard from "react-copy-to-clipboard"
import { QuickWinIssueRow } from "../QuickWinIssueRow"

import styles from "./QuickWinCard.module.scss"

import type { IssuesSummary } from "@/molecules/IssuesSummaryFC/IssuesSummary.types"
import type { QuickWinCardProps } from "."

type DismissPhase = "idle" | "spinning" | "collapsing"

export const QuickWinCard = ({
  quickWin,
  projectName,
  onIgnore,
  onIssueClick
}: QuickWinCardProps) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const { logger, EVENTS } = useLogger()
  const convertors = useNumberConvertor()
  const [expanded, setExpanded] = useState(false)
  const [dismissPhase, setDismissPhase] = useState<DismissPhase>("idle")
  const containerRef = useRef<HTMLDivElement>(null)
  const spinTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const mountedRef = useRef(true)

  const {
    id,
    projectId,
    sourceFilePath,
    sourceControlLink,
    language,
    totalIssues,
    issuesSummary,
    severityCounts,
    isIgnored,
    type,
    dependencyName,
    currentVersion,
    fixVersion
  } = quickWin

  const cardMeta = useMemo(
    () => ({ projectId, quickWinId: id, type, totalIssues, issuesSummary }),
    [projectId, id, type, totalIssues, issuesSummary]
  )

  const { issues, hasNext, isFetching, loadMore, reset } = useQuickWinIssues(
    id,
    expanded
  )

  const handleViewIssues = () => {
    logger.info(EVENTS.ANALYTIC_EVENTS.QUICK_WINS.CARD.VIEW_ISSUES, cardMeta)
    setExpanded(true)
    reset()
  }

  const handleHideIssues = () => {
    logger.info(EVENTS.ANALYTIC_EVENTS.QUICK_WINS.CARD.HIDE_ISSUES, {
      projectId,
      quickWinId: id
    })
    setExpanded(false)
  }

  const [loadingMore, setLoadingMore] = useState(false)
  const issueCountAtClick = useRef(0)

  useEffect(() => {
    if (loadingMore && issues.length > issueCountAtClick.current) {
      const timer = setTimeout(() => setLoadingMore(false), 600)
      return () => clearTimeout(timer)
    }
  }, [issues.length, loadingMore])

  const handleShowMore = () => {
    logger.info(EVENTS.ANALYTIC_EVENTS.QUICK_WINS.CARD.SHOW_MORE_ISSUES, {
      ...cardMeta,
      page: "next"
    })
    issueCountAtClick.current = issues.length
    setLoadingMore(true)
    loadMore()
  }

  const isPackage = isPackageUpdate(type)

  const copyText = buildQuickWinCopyText(quickWin, projectName, t)

  const handleCopy = () => {
    logger.info(EVENTS.ANALYTIC_EVENTS.QUICK_WINS.CARD.FIX, {
      ...cardMeta,
      fixType: "copy_fix",
      dependencyName,
      currentVersion,
      fixVersion
    })
    message.info(t("quickWins.fixCopied"))
  }

  useEffect(
    () => () => {
      mountedRef.current = false
      clearTimeout(spinTimerRef.current)
    },
    []
  )

  const handleIgnore = useCallback(() => {
    if (dismissPhase !== "idle") return

    const event = isIgnored
      ? EVENTS.ANALYTIC_EVENTS.QUICK_WINS.CARD.RESTORE
      : EVENTS.ANALYTIC_EVENTS.QUICK_WINS.CARD.IGNORE
    logger.info(event, { ...cardMeta, dependencyName })

    setDismissPhase("spinning")
    onIgnore(id, !isIgnored)

    spinTimerRef.current = setTimeout(() => {
      if (!mountedRef.current) return
      if (containerRef.current) {
        containerRef.current.style.height = `${containerRef.current.offsetHeight}px`
        void containerRef.current.offsetHeight
      }
      setDismissPhase("collapsing")
    }, 600)
  }, [
    dismissPhase,
    onIgnore,
    id,
    isIgnored,
    logger,
    EVENTS,
    cardMeta,
    dependencyName
  ])

  const handleIssueClick = useCallback(
    (issueId: string) => {
      logger.info(EVENTS.ANALYTIC_EVENTS.QUICK_WINS.CARD.ISSUE_CLICK, {
        ...cardMeta,
        issueId
      })
      onIssueClick(issueId)
    },
    [logger, EVENTS, cardMeta, onIssueClick]
  )

  const TypeIcon = isPackage ? Package : BaseImage

  return (
    <div
      ref={containerRef}
      className={cn(
        styles.container,
        dismissPhase === "collapsing" && styles.collapsing
      )}
    >
      {dismissPhase !== "idle" && (
        <div className={styles.dismissOverlay}>
          <Spin size="small" />
        </div>
      )}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <span className={styles.title}>
            {t(quickWin.titleKey, {
              prefix: quickWin.titleParams.prefix
                ? `${quickWin.titleParams.prefix} `
                : "",
              issueCount: convertors.abbreviateNumber(
                quickWin.titleParams.count
              )
            })}
          </span>
          <span className={cn(styles.updateBadge, isPackage && styles.package)}>
            <TypeIcon className={styles.badgeIcon} />
            {isPackage
              ? t("quickWins.filter.packageUpdate")
              : t("quickWins.filter.baseImageUpdate")}
          </span>
          <div className={styles.actions}>
            <CopyToClipboard text={copyText} onCopy={handleCopy}>
              <Button
                type="primary"
                size="small"
                icon={
                  <QuickWinBolt
                    width={12}
                    height={14}
                    fill="white"
                    innerFill="transparent"
                    strokeWidth={0.6}
                    className={styles.fixIcon}
                  />
                }
              >
                {t("quickWins.fixIt")}
              </Button>
            </CopyToClipboard>
            <Tooltip
              title={isIgnored ? t("quickWins.restore") : t("quickWins.ignore")}
            >
              <Button
                type="default"
                size="small"
                icon={isIgnored ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                onClick={handleIgnore}
                aria-label={
                  isIgnored
                    ? t("quickWins.tabs.suggested")
                    : t("quickWins.tabs.ignored")
                }
              />
            </Tooltip>
          </div>
        </div>
        {isPackage && (
          <span className={styles.source}>
            <span>{t("quickWins.card.sourceLabel")}:</span>
            {sourceControlLink ? (
              <Link
                href={sourceControlLink}
                target="_blank"
                className={styles.sourceLink}
              >
                {sourceFilePath}
              </Link>
            ) : (
              <span className={styles.sourcePath}>{sourceFilePath}</span>
            )}
          </span>
        )}
        <div className={styles.versionRow}>
          <TypeIcon className={styles.versionIcon} />
          <span className={styles.versionPath}>
            {dependencyName}:{currentVersion}
          </span>
          <ArrowRightOutlined className={styles.versionArrow} />
          <span className={styles.versionPath}>{fixVersion}</span>
        </div>
        <div className={styles.issuesRow}>
          <span className={styles.treeConnector} />
          <span className={styles.issuesCount}>
            {t("quickWins.card.issues", {
              issueCount: convertors.abbreviateNumber(totalIssues)
            })}
          </span>
        </div>
        <div className={styles.severityRow}>
          <IssuesSummaryFC
            noFixWidth
            convertors={convertors}
            issues={severityCounts as IssuesSummary}
          />
        </div>
      </div>

      {expanded && (
        <div className={styles.issuesList}>
          {isFetching && issues.length === 0 ? (
            Array.from({ length: Math.min(totalIssues, 5) }).map((_, i) => (
              <div key={i} className={styles.skeletonRow}>
                <Skeleton.Avatar
                  active
                  size={20}
                  shape="square"
                  className={styles.skeletonAvatar}
                />
                <Skeleton
                  active
                  title={false}
                  paragraph={{ rows: 2, width: ["60%", "40%"] }}
                  className={styles.skeletonBody}
                />
              </div>
            ))
          ) : (
            <div className={styles.issuesScroll}>
              {issues.map((issue) => (
                <div key={issue.id} className={styles.issueItem}>
                  <QuickWinIssueRow
                    issue={issue}
                    language={language}
                    onClick={() => handleIssueClick(issue.id)}
                  />
                </div>
              ))}
              {(hasNext || loadingMore) && (
                <Button
                  type="link"
                  size="small"
                  loading={loadingMore}
                  onClick={handleShowMore}
                  disabled={loadingMore}
                  className={styles.showMoreButton}
                >
                  {t("quickWins.showMore")}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      <div className={styles.footer}>
        <Button
          type="link"
          className={styles.toggleButton}
          onClick={expanded ? handleHideIssues : handleViewIssues}
        >
          {expanded ? t("quickWins.hideIssues") : t("quickWins.viewIssues")}
        </Button>
      </div>
    </div>
  )
}
